import { FormattedMessage as FM } from "react-intl";
import { ArrowLeftIcon, ArrowRightIcon } from "components/Icons";
import { cn } from "tools/ui";
import { useTable } from "storev2/tableStore";

const baseButton =
  "flex items-center justify-center min-w-9 h-9 px-2 rounded-lg text-sm font-medium transition-colors select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400";
const arrowButton =
  "text-neutral-600 hover:bg-neutral-100 disabled:text-neutral-300 disabled:hover:bg-transparent disabled:cursor-not-allowed cursor-pointer";

function getVisiblePages(currentPage, numPages) {
  const maxVisiblePages = 9;
  if (numPages <= maxVisiblePages) {
    return Array.from({ length: numPages }, (_, i) => i + 1);
  }

  const windowSize = 5;
  const rangeOffset = Math.floor((windowSize - 1) / 2);
  let winStart = Math.max(2, currentPage - rangeOffset);
  let winEnd = winStart + windowSize - 1;
  if (winEnd > numPages - 1) {
    winEnd = numPages - 1;
    winStart = winEnd - windowSize + 1;
  }

  const pages = [1];
  if (winStart > 2) pages.push("...");
  for (let i = winStart; i <= winEnd; i++) pages.push(i);
  if (winEnd < numPages - 1) pages.push("...");
  pages.push(numPages);
  return pages;
}

/**
 *
 * @param {*} props
 * @returns
 */

function Pagination(props) {
  const { limit, offset, lenRecords, storeId } = props;
  const table = useTable((state) => state[storeId]);
  const countTotal = table?.count ?? 0;

  let firstItem = 1;
  let lastItem = null;
  if (lenRecords) {
    firstItem = (offset ?? 0) + 1;
    lastItem = (offset ?? 0) + lenRecords;
  }

  const pageLimit = limit || countTotal || 1;
  const numPages = Math.ceil(countTotal / pageLimit);
  if (numPages <= 1 || !isFinite(numPages)) {
    return null;
  }

  const currentPage = Math.ceil(firstItem / (limit || 1));
  const isFirst = currentPage <= 1;
  const isLast = currentPage >= numPages;

  function onClick(value) {
    if (value === "previous" && isFirst) return;
    if (value === "next" && isLast) return;
    props.onChangePage(value);
  }

  function handlePage(page) {
    if (page === currentPage) return;
    props.onChangePage(null, page);
  }

  const visiblePages = getVisiblePages(currentPage, numPages);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-neutral-200 bg-white px-4 py-3 sm:px-6">
      <p className="text-sm text-neutral-500">
        <FM
          id="pagination.results"
          values={{
            firstItem: (
              <span className="font-semibold text-neutral-700">
                {firstItem}
              </span>
            ),
            lastItem: (
              <span className="font-semibold text-neutral-700">
                {lastItem}
              </span>
            ),
            countTotal: (
              <span className="font-semibold text-neutral-700">
                {countTotal}
              </span>
            ),
          }}
        />
      </p>
      <nav className="flex items-center gap-1" aria-label="Pagination">
        <button
          type="button"
          onClick={() => onClick("previous")}
          disabled={isFirst}
          aria-label="Previous"
          className={cn(baseButton, arrowButton)}
        >
          <ArrowLeftIcon className="size-4 fill-current" aria-hidden="true" />
        </button>

        <span className="sm:hidden px-2 text-sm text-neutral-600">
          <span className="font-semibold text-neutral-800">{currentPage}</span>
          {" / "}
          {numPages}
        </span>

        <div className="hidden sm:flex items-center gap-1">
          {visiblePages.map((page, idx) => {
            if (page === "...") {
              return (
                <span
                  key={`e-${idx}`}
                  className="flex items-end justify-center w-6 h-9 pb-2 text-neutral-400 select-none"
                >
                  …
                </span>
              );
            }
            const isCurrent = page === currentPage;
            return (
              <button
                type="button"
                key={page}
                onClick={() => handlePage(page)}
                aria-current={isCurrent ? "page" : undefined}
                className={cn(
                  baseButton,
                  isCurrent
                    ? "bg-neutral-800 text-white shadow-sm cursor-default"
                    : "text-neutral-600 hover:bg-neutral-100 cursor-pointer",
                )}
              >
                {page}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => onClick("next")}
          disabled={isLast}
          aria-label="Next"
          className={cn(baseButton, arrowButton)}
        >
          <ArrowRightIcon className="size-4 fill-current" aria-hidden="true" />
        </button>
      </nav>
    </div>
  );
}

export default Pagination;
